import { PaperAirplaneIcon } from "@heroicons/react/24/outline";
import moment from "moment";
import { useCallback, useMemo, useState } from "react";
import {
  Button,
  ChatBubble,
  Form,
  Input,
  Join,
  Pagination,
  Select,
} from "react-daisyui";
import { types } from "../../graphql";
import { Action, checkAuthorization } from "../../utils/authorization";
import { BlockProps } from "../../containers/Block";
import { flattenInfiniteData } from "../../utils/infiniteQuery";

type InstantMessagingInteractionData = {
  message: string;
};

export default function InstantMessaging({
  zone,
  block,
  query,
  participants,
  addInteractionMutation,
}: BlockProps) {
  const [message, setMessage] = useState("");
  const [currentPage, setCurrentPage] = useState(0);

  const pageSize = 15;

  const allInteractions = useMemo(
    () => flattenInfiniteData(query.data, (p) => p.interactions?.items),
    [query.data],
  );

  const collectCurrentPageInteractions =
    useCallback((): types.Interaction[] => {
      return allInteractions.slice(
        currentPage * pageSize,
        (currentPage + 1) * pageSize,
      );
    }, [allInteractions, currentPage]);

  const totalPages = useMemo(
    () => Math.ceil(allInteractions.length / pageSize),
    [allInteractions.length],
  );

  const toInteractionModel = useCallback(
    (interaction: types.Interaction) => ({
      ...interaction,
      data: (interaction.data?.trim()
        ? JSON.parse(interaction.data)
        : null) as InstantMessagingInteractionData,
      sender: participants.find(
        (p) => p.participantId === interaction.participantId,
      ),
    }),
    [participants],
  );

  return (
    <div>
      {collectCurrentPageInteractions()
        .reverse()
        .map(toInteractionModel)
        .map((interaction) => (
          <ChatBubble
            key={interaction.interactionKey}
            end={
              interaction.sender?.participantId ===
              zone.participation?.participantId
            }
          >
            <ChatBubble.Header>
              {interaction.sender?.username ?? "Unknown"}
              <ChatBubble.Time className="ms-2">
                {moment(interaction.timestamp).fromNow()}
              </ChatBubble.Time>
            </ChatBubble.Header>
            <ChatBubble.Message
              color={
                interaction.sender?.participantId ===
                zone.participation?.participantId
                  ? "primary"
                  : "ghost"
              }
            >
              {interaction.data.message}
            </ChatBubble.Message>
          </ChatBubble>
        ))}
      <Pagination className="my-4">
        <Button
          className="join-item"
          disabled={currentPage === 0}
          onClick={() => setCurrentPage((currentPage) => currentPage - 1)}
        >
          Newer
        </Button>
        <Select
          className="btn join-item border-0 bg-base-200"
          value={currentPage.toString()}
          onChange={(e) => {
            const page = parseInt(e.target.value);
            if (page > totalPages - 3 && query.hasNextPage) {
              query.fetchNextPage();
            }
            setCurrentPage(page);
          }}
        >
          {Array.from(Array(totalPages)).map((i, page) => (
            <Select.Option value={page} key={page}>{`Page ${
              page + 1
            }`}</Select.Option>
          ))}
        </Select>
        <Button
          className="join-item"
          disabled={currentPage >= totalPages - 1}
          onClick={() => {
            if (currentPage > totalPages - 3 && query.hasNextPage) {
              query.fetchNextPage();
            }
            setCurrentPage((currentPage) => currentPage + 1);
          }}
        >
          Older
        </Button>
      </Pagination>
      <Form
        onSubmit={(e) => {
          e.preventDefault();
          setMessage("");
          addInteractionMutation.mutate({
            blockId: block.blockId,
            interactionKey: `${new Date().getTime()}:${zone.participation
              ?.participantId}`,
            data: JSON.stringify({ message }),
            participantId: zone.participation?.participantId,
            timestamp: new Date().getTime(),
            zoneId: block.zoneId,
          } as types.AddInteractionMutationVariables);
        }}
      >
        <Join horizontal>
          <Input
            type="text"
            onChange={(e) => setMessage(e.target.value)}
            value={message}
            disabled={
              !checkAuthorization(zone.participation, block, Action.WRITE)
            }
            className="join-item"
          />
          <Button
            type="submit"
            disabled={!message.trim()}
            className="btn-primary join-item"
          >
            <PaperAirplaneIcon className="h-6 w-6" />
          </Button>
        </Join>
      </Form>
    </div>
  );
}
